import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, CheckCircle, X, Plus, Trash2, Save, Calculator } from 'lucide-react';
import axiosInstance from '../../variables/api/axiosInstance';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import Footer from '../../components/Footer';
import { toast } from 'react-toastify';

const GestionPaiementSinistreExperts = () => {
    const [experts, setExperts] = useState([]);
    const [sinistres, setSinistres] = useState([]);
    const [expertSelectionne, setExpertSelectionne] = useState(null);
    const [tauxRetenue, setTauxRetenue] = useState(5);
    const [dossiersSelectionnes, setDossiersSelectionnes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchExpert, setSearchExpert] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [expertsAffiches, setExpertsAffiches] = useState(5);

    // ✅ FIX: Sélection d'expert — reset SYNCHRONE immédiat avant tout re-render
    const handleSelectExpert = (expert) => {
        // Tout réinitialiser d'un coup dans le même cycle de rendu
        setSinistres([]);
        setDossiersSelectionnes([]);
        setSearchTerm('');
        setTauxRetenue(expert.tauxRetenue ?? 5);
        setExpertSelectionne(expert);
    };

    // Chargement des sinistres après sélection d'expert
    useEffect(() => {
        if (expertSelectionne) {
            axiosInstance.get(`/api/sinistre/expertise/${expertSelectionne?.codeId}`)
                .then(response => {
                    console.log("Réponse brute sinistres:", response.data);
                    console.log("Type:", typeof response.data, "| isArray:", Array.isArray(response.data));

                    // Normalisation : gère tableau direct OU objet paginé {content, data, items...}
                    const data = Array.isArray(response.data)
                        ? response.data
                        : response.data?.content
                        ?? response.data?.data
                        ?? response.data?.items
                        ?? [];

                    setSinistres(data);
                })
                .catch(error => {
                    console.error(error);
                    toast.error("Une erreur est survenue lors de la recherche des sinistres.");
                });
        } else {
            setSinistres([]);
            setDossiersSelectionnes([]);
        }
    }, [expertSelectionne]);

    useEffect(() => {
        axiosInstance.get('/api/expertise/expert/all')
            .then(response => {
                setExperts(response.data);
            })
            .catch(error => {
                console.error(error);
                toast.error("Une erreur est survenue lors de la recherche des experts.");
            });
    }, []);

    const ajouterDossier = (dossier) => {
        if (!dossiersSelectionnes.find(d => d.codeId === dossier.codeId)) {
            setDossiersSelectionnes([
                ...dossiersSelectionnes,
                {
                    ...dossier,
                    honoraires: 0,
                    fraisAccessoires: 0,
                }
            ]);
        }
    };

    const retirerDossier = (dossierId) => {
        setDossiersSelectionnes(dossiersSelectionnes.filter(d => d.codeId !== dossierId));
    };

    const mettreAJourMontant = (dossierId, champ, valeur) => {
        setDossiersSelectionnes(dossiersSelectionnes.map(d =>
            d.codeId === dossierId ? { ...d, [champ]: parseFloat(valeur) || 0 } : d
        ));
    };

    const verifierMontant = (dossier) => {
        const total = dossier.honoraires + dossier.fraisAccessoires;
        return total <= dossier.sap;
    };

    const calculerTotaux = () => {
        const totalHonoraires = dossiersSelectionnes.reduce((acc, d) => acc + d.honoraires, 0);
        const totalFrais = dossiersSelectionnes.reduce((acc, d) => acc + d.fraisAccessoires, 0);
        const totalBrut = totalHonoraires + totalFrais;
        // La retenue ne porte QUE sur les honoraires ; les frais accessoires sont payés à valeur faciale.
        const montantRetenue = totalHonoraires * (tauxRetenue / 100);
        const totalNet = totalBrut - montantRetenue;

        return { totalHonoraires, totalFrais, totalBrut, montantRetenue, totalNet };
    };

    const validerReglement = () => {
        if (dossiersSelectionnes.length === 0) return false;
        const erreurs = dossiersSelectionnes.filter(d => !verifierMontant(d));
        return erreurs.length === 0;
    };

    // Anti double-clic : verrouille le bouton pendant l'enregistrement.
    const [saving, setSaving] = useState(false);

    const enregistrerReglement = () => {
        if (saving) return;
        if (validerReglement()) {
            const dd = dossiersSelectionnes.map(d => ({
                codeId: d.codeId,
                honoraires: d.honoraires,
                fraisAccessoires: d.fraisAccessoires,
                retenue: tauxRetenue,
                expertCodeId: expertSelectionne.codeId
            }));

            setSaving(true);
            axiosInstance.post(`/api/decaissement/all-experts`, JSON.stringify(dd))
                .then(() => {
                    toast.success("Règlement enregistré avec succès");
                    setDossiersSelectionnes([]);
                    setExpertSelectionne(null);
                })
                .catch(error => {
                    console.error(error);
                    toast.error("Une erreur est survenue lors de l'enregistrement du règlement.");
                })
                .finally(() => setSaving(false));
        }
    };

    const totaux = calculerTotaux();

    // Filtrage des experts
    const expertsFiltres = experts.filter(expert => {
        const nomComplet = `${expert?.prenom || ""} ${expert?.nom || ""}`.toLowerCase();
        const recherche = searchExpert?.toLowerCase() || "";
        return nomComplet.includes(recherche) ||
            expert?.nom?.toLowerCase().includes(recherche) ||
            expert?.prenom?.toLowerCase().includes(recherche) ||
            expert?.fonction?.toLowerCase().includes(recherche);
    });

    const expertsAfficher = expertsFiltres.slice(0, expertsAffiches);
    const aAutresExperts = expertsFiltres.length > expertsAffiches;

    // ✅ FIX: sinistres est maintenant garanti comme tableau grâce à la normalisation
    const dossiersDisponibles = sinistres.filter(d =>
        !dossiersSelectionnes.find(ds => ds.codeId === d.codeId) &&
        (d?.numSinistre?.toLowerCase().includes(searchTerm.toLowerCase()) || d?.numeroSinistreM?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{
                                minHeight: '100vh',
                                backgroundColor: '#F9FBF9',
                                padding: '24px',
                                fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}>
                                {/* Header */}
                                <div style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    marginBottom: '24px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }}>
                                    <h1 style={{
                                        fontSize: '28px',
                                        fontWeight: '700',
                                        color: '#333333',
                                        margin: '0 0 8px 0'
                                    }}>
                                        Nouveau Règlement d'Expert
                                    </h1>
                                    <p style={{
                                        color: '#666',
                                        margin: 0,
                                        fontSize: '14px'
                                    }}>
                                        Sélectionnez un expert et ajoutez les dossiers à régler
                                    </p>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                                    {/* Colonne gauche */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                        {/* Sélection Expert */}
                                        <div style={{
                                            backgroundColor: 'white',
                                            borderRadius: '12px',
                                            padding: '24px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                        }}>
                                            <h2 style={{
                                                fontSize: '18px',
                                                fontWeight: '600',
                                                color: '#333333',
                                                margin: '0 0 16px 0'
                                            }}>
                                                Sélection de l'Expert
                                            </h2>

                                            <div style={{ position: 'relative', marginBottom: '16px' }}>
                                                <Search size={18} style={{
                                                    position: 'absolute',
                                                    left: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: '#999'
                                                }} />
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher un expert (nom, prénom, spécialité)..."
                                                    value={searchExpert}
                                                    onChange={(e) => {
                                                        setSearchExpert(e.target.value);
                                                        setExpertsAffiches(5);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px 12px 10px 40px',
                                                        border: '1px solid #E0E0E0',
                                                        borderRadius: '6px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                paddingRight: '4px'
                                            }}>
                                                {expertsAfficher.map(expert => (
                                                    <div
                                                        key={expert?.codeId}
                                                        onClick={() => handleSelectExpert(expert)}
                                                        style={{
                                                            padding: '16px',
                                                            border: expertSelectionne?.codeId === expert.codeId
                                                                ? '2px solid #2ECC71'
                                                                : '1px solid #E0E0E0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            backgroundColor: expertSelectionne?.codeId === expert?.codeId
                                                                ? '#A8E6CF20'
                                                                : 'white'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (expertSelectionne?.codeId !== expert?.codeId) {
                                                                e.currentTarget.style.borderColor = '#2ECC71';
                                                                e.currentTarget.style.backgroundColor = '#A8E6CF10';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (expertSelectionne?.codeId !== expert?.codeId) {
                                                                e.currentTarget.style.borderColor = '#E0E0E0';
                                                                e.currentTarget.style.backgroundColor = 'white';
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: '600', color: '#333333', marginBottom: '4px' }}>
                                                            {expert.prenom} {expert.nom}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#666' }}>
                                                            {expert.fonction} • Taux de retenue: {expert.tauxRetenue}%
                                                        </div>
                                                    </div>
                                                ))}

                                                {expertsFiltres.length === 0 && (
                                                    <div style={{
                                                        textAlign: 'center',
                                                        padding: '20px',
                                                        color: '#999',
                                                        fontSize: '14px'
                                                    }}>
                                                        Aucun expert trouvé
                                                    </div>
                                                )}

                                                {aAutresExperts && (
                                                    <button
                                                        onClick={() => setExpertsAffiches(prev => prev + 5)}
                                                        style={{
                                                            padding: '12px',
                                                            backgroundColor: 'transparent',
                                                            border: '1px dashed #2ECC71',
                                                            borderRadius: '6px',
                                                            color: '#2ECC71',
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#A8E6CF20';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        Afficher plus d'experts ({expertsFiltres.length - expertsAffiches} restants)
                                                    </button>
                                                )}
                                            </div>

                                            {expertSelectionne && (
                                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E0E0E0' }}>
                                                    <label style={{
                                                        display: 'block',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        color: '#333333',
                                                        marginBottom: '8px'
                                                    }}>
                                                        Taux de retenue pour ce règlement (%)
                                                    </label>
                                                    <select
                                                        value={tauxRetenue}
                                                        onChange={(e) => setTauxRetenue(parseFloat(e.target.value))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px',
                                                            border: '1px solid #E0E0E0',
                                                            borderRadius: '6px',
                                                            fontSize: '14px',
                                                            color: '#333333',
                                                            backgroundColor: 'white',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <option value={3}>3%</option>
                                                        <option value={5}>5%</option>
                                                        <option value={20}>20%</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>

                                        {/* Liste des dossiers disponibles */}
                                        {expertSelectionne && (
                                            <div style={{
                                                backgroundColor: 'white',
                                                borderRadius: '12px',
                                                padding: '24px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                maxHeight: '500px',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <h2 style={{
                                                    fontSize: '18px',
                                                    fontWeight: '600',
                                                    color: '#333333',
                                                    margin: '0 0 4px 0'
                                                }}>
                                                    Dossiers Disponibles
                                                </h2>

                                                {/* ✅ Affichage du nombre réel de dossiers */}
                                                <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px 0' }}>
                                                    {sinistres.length} dossier{sinistres.length > 1 ? 's' : ''} trouvé{sinistres.length > 1 ? 's' : ''} pour cet expert
                                                </p>

                                                <div style={{ position: 'relative', marginBottom: '16px' }}>
                                                    <Search size={18} style={{
                                                        position: 'absolute',
                                                        left: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        color: '#999'
                                                    }} />
                                                    <input
                                                        type="text"
                                                        placeholder="Rechercher un dossier..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px 10px 40px',
                                                            border: '1px solid #E0E0E0',
                                                            borderRadius: '6px',
                                                            fontSize: '14px'
                                                        }}
                                                    />
                                                </div>

                                                <div style={{ flex: 1, overflowY: 'auto' }}>
                                                    {dossiersDisponibles.length === 0 ? (
                                                        <div style={{
                                                            textAlign: 'center',
                                                            padding: '20px',
                                                            color: '#999',
                                                            fontSize: '14px'
                                                        }}>
                                                            {sinistres.length === 0
                                                                ? "Aucun dossier disponible pour cet expert"
                                                                : "Tous les dossiers ont été ajoutés"}
                                                        </div>
                                                    ) : (
                                                        dossiersDisponibles.map(dossier => (
                                                            <div
                                                                key={dossier?.codeId}
                                                                onClick={() => ajouterDossier(dossier)}
                                                                style={{
                                                                    padding: '12px',
                                                                    border: '1px solid #E0E0E0',
                                                                    borderRadius: '6px',
                                                                    marginBottom: '8px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.borderColor = '#2ECC71';
                                                                    e.currentTarget.style.backgroundColor = '#A8E6CF10';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.borderColor = '#E0E0E0';
                                                                    e.currentTarget.style.backgroundColor = 'white';
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <div>
                                                                        <div style={{ fontWeight: '600', color: '#333333', fontSize: '14px' }}>
                                                                            {dossier.numSinistre || dossier.numeroSinistreM}
                                                                        </div>
                                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                                            {dossier.type} • SAP: {dossier.sap?.toLocaleString('fr-FR')} FCFA
                                                                        </div>
                                                                    </div>
                                                                    <Plus size={18} style={{ color: '#2ECC71' }} />
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Colonne droite */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxHeight: 'calc(100vh - 200px)'
                                    }}>
                                        <h2 style={{
                                            fontSize: '18px',
                                            fontWeight: '600',
                                            color: '#333333',
                                            margin: '0 0 16px 0'
                                        }}>
                                            Dossiers à Régler ({dossiersSelectionnes.length})
                                        </h2>

                                        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
                                            {dossiersSelectionnes.length === 0 ? (
                                                <div style={{
                                                    textAlign: 'center',
                                                    padding: '40px 20px',
                                                    color: '#999'
                                                }}>
                                                    Aucun dossier sélectionné
                                                </div>
                                            ) : (
                                                dossiersSelectionnes.map(dossier => {
                                                    const estValide = verifierMontant(dossier);
                                                    const total = dossier.honoraires + dossier.fraisAccessoires;

                                                    return (
                                                        <div
                                                            key={dossier?.codeId}
                                                            style={{
                                                                padding: '16px',
                                                                border: estValide ? '1px solid #E0E0E0' : '2px solid #e74c3c',
                                                                borderRadius: '8px',
                                                                marginBottom: '12px',
                                                                backgroundColor: estValide ? 'white' : '#ffe5e5'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                                                                <div>
                                                                    <div style={{ fontWeight: '600', color: '#333333', fontSize: '14px' }}>
                                                                        {dossier.numSinistre || dossier.numeroSinistreM}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                                        SAP: {dossier.sap?.toLocaleString('fr-FR')} FCFA
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => retirerDossier(dossier?.codeId)}
                                                                    style={{
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        padding: '4px',
                                                                        color: '#e74c3c'
                                                                    }}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>

                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                                <div>
                                                                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                                                                        Honoraires (FCFA)
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={dossier.honoraires || ''}
                                                                        onChange={(e) => mettreAJourMontant(dossier?.codeId, 'honoraires', e.target.value)}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '8px',
                                                                            border: '1px solid #E0E0E0',
                                                                            borderRadius: '4px',
                                                                            fontSize: '14px'
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                                                                        Frais Accessoires (FCFA)
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={dossier.fraisAccessoires || ''}
                                                                        onChange={(e) => mettreAJourMontant(dossier?.codeId, 'fraisAccessoires', e.target.value)}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '8px',
                                                                            border: '1px solid #E0E0E0',
                                                                            borderRadius: '4px',
                                                                            fontSize: '14px'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div style={{
                                                                marginTop: '12px',
                                                                paddingTop: '12px',
                                                                borderTop: '1px solid #E0E0E0',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#333333' }}>
                                                                    Total: {total.toLocaleString('fr-FR')} FCFA
                                                                </span>
                                                                {estValide ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2ECC71' }}>
                                                                        <CheckCircle size={16} />
                                                                        <span style={{ fontSize: '12px' }}>Valide</span>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#e74c3c' }}>
                                                                        <AlertCircle size={16} />
                                                                        <span style={{ fontSize: '12px' }}>Dépasse le SAP</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>

                                        {/* Récapitulatif */}
                                        {dossiersSelectionnes.length > 0 && (
                                            <>
                                                <div style={{
                                                    padding: '16px',
                                                    backgroundColor: '#A8E6CF20',
                                                    borderRadius: '8px',
                                                    marginBottom: '16px'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                        <Calculator size={18} style={{ color: '#2ECC71' }} />
                                                        <span style={{ fontWeight: '600', color: '#333333' }}>Récapitulatif</span>
                                                    </div>

                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span style={{ color: '#666' }}>Total Honoraires:</span>
                                                            <span style={{ fontWeight: '600', color: '#333333' }}>
                                                                {totaux.totalHonoraires.toLocaleString('fr-FR')} FCFA
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span style={{ color: '#666' }}>Total Frais:</span>
                                                            <span style={{ fontWeight: '600', color: '#333333' }}>
                                                                {totaux.totalFrais.toLocaleString('fr-FR')} FCFA
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #2ECC71' }}>
                                                            <span style={{ color: '#666' }}>Montant Brut:</span>
                                                            <span style={{ fontWeight: '600', color: '#333333' }}>
                                                                {totaux.totalBrut.toLocaleString('fr-FR')} FCFA
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span style={{ color: '#666' }}>Retenue ({tauxRetenue}%):</span>
                                                            <span style={{ fontWeight: '600', color: '#e74c3c' }}>
                                                                - {totaux.montantRetenue.toLocaleString('fr-FR')} FCFA
                                                            </span>
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            paddingTop: '8px',
                                                            borderTop: '2px solid #2ECC71',
                                                            fontSize: '16px'
                                                        }}>
                                                            <span style={{ fontWeight: '600', color: '#145A32' }}>Montant Net:</span>
                                                            <span style={{ fontWeight: '700', color: '#145A32' }}>
                                                                {totaux.totalNet.toLocaleString('fr-FR')} FCFA
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={enregistrerReglement}
                                                    disabled={saving || !validerReglement()}
                                                    style={{
                                                        width: '100%',
                                                        padding: '14px',
                                                        backgroundColor: (saving || !validerReglement()) ? '#ccc' : '#2ECC71',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        fontSize: '16px',
                                                        fontWeight: '600',
                                                        cursor: (saving || !validerReglement()) ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (validerReglement()) {
                                                            e.currentTarget.style.backgroundColor = '#27AE60';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (validerReglement()) {
                                                            e.currentTarget.style.backgroundColor = '#2ECC71';
                                                        }
                                                    }}
                                                >
                                                    <Save size={20} />
                                                    {saving ? "Enregistrement…" : "Enregistrer le Règlement"}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Toast de succès */}
                                {showSuccess && (
                                    <div style={{
                                        position: 'fixed',
                                        top: '24px',
                                        right: '24px',
                                        backgroundColor: '#2ECC71',
                                        color: 'white',
                                        padding: '16px 24px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        zIndex: 1000
                                    }}>
                                        <CheckCircle size={24} />
                                        <span style={{ fontWeight: '600' }}>Règlement enregistré avec succès!</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default GestionPaiementSinistreExperts;